import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesComments, actesAddComment, actesRemoveComment,
  type ActesNote, type ActesReference,
} from '@/integrations/actesApi';
import { ActesAvatar } from './ActesPerson';
import { actesEn } from '@/lib/actesCountry';

/**
 * LES NOTES — sur un dossier, sur une demande.
 *
 * Two things were missing and both were load-bearing.
 *
 * TAGGING SOMEBODY. A note with a name on it is a small task: the person is
 * notified and comes to read it. The database has carried `mentioned_to`
 * since the migration and the trigger that notifies has been firing for
 * nothing, because no screen ever sent one. Writing "@" into a note and
 * hoping is not the same as telling somebody.
 *
 * REMOVING ONE. A note posted by mistake — the wrong dossier, half a sentence,
 * a client's name in the wrong file — could not be taken back at all. Your own
 * notes, or anybody's for a super admin; the server decides, the button only
 * asks. And it asks: a note is somebody's writing, so removing it confirms.
 */
export function Notes({ lang, reference, dossierId, acteId, compact }: {
  lang: Lang;
  reference: ActesReference;
  dossierId?: string;
  acteId?: string;
  /** Dans une fiche en lecture, le cadre d'écriture ouvert en permanence
   *  occupait deux cents pixels pour dire « aucune note ». En mode compact il
   *  reste derrière un bouton et ne s'ouvre que si on a quelque chose à
   *  écrire. */
  compact?: boolean;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const target = dossierId ? { dossierId } : { acteId: acteId as string };
  const key = dossierId ? ['actes', 'comments', 'dossier', dossierId] : ['actes', 'comments', 'acte', acteId];

  const notes = useQuery<ActesNote[]>({
    queryKey: key as string[],
    queryFn: ({ signal }) => actesComments(target, signal),
    staleTime: 30_000,
  });

  const [draft, setDraft] = useState('');
  const [notify, setNotify] = useState<string>('');
  const [who, setWho] = useState(false);
  const [focus, setFocus] = useState(false);
  const [writing, setWriting] = useState(false);
  const [ask, setAsk] = useState<ActesNote | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const box = useRef<HTMLTextAreaElement>(null);

  const person = (id: string) => reference.profiles.find((p) => p.id === id);
  const mentioned = notify ? person(notify) : null;

  const add = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true); setErr('');
    try {
      await actesAddComment({ ...(dossierId ? { dossier_id: dossierId } : { acte_request_id: acteId }), body, mentioned_to: notify || null });
      setDraft(''); setNotify('');
      if (compact) setWriting(false);
      await notes.refetch();
    } catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const remove = async () => {
    if (!ask) return;
    setBusy(true); setErr('');
    try { await actesRemoveComment(ask.id); setAsk(null); await notes.refetch(); }
    catch (e) { setErr(friendlyError(e, lang)); }
    setBusy(false);
  };

  const when = (iso: string) =>
    new Date(iso).toLocaleString(lang === 'fr' ? 'fr-FR' : 'en-GB',
      { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  /* Yours, or anybody's when you run the place. The button simply does not
     appear otherwise — offering it and then refusing is worse. */
  const mine = (n: ActesNote) =>
    reference.me.isSuper || (!!n.author_id && n.author_id === reference.me.id);

  return (
    <div className="actes-notes">
      {notes.isLoading ? (
        <p className="muted-note">{T('Chargement…', 'Loading…')}</p>
      ) : (notes.data ?? []).length === 0 ? (
        <p className="muted-note">{T('Aucune note.', 'No note.')}</p>
      ) : (notes.data ?? []).map((n) => (
        <div key={n.id} className="actes-note">
          <div className="actes-note-h">
            <strong>{n.author_name ?? '—'}</strong>
            <span className="dim">{when(n.created_at)}</span>
            {n.mentioned_to && (
              <span className="actes-note-at" title={T('Personne prévenue', 'Person notified')}>
                @{person(n.mentioned_to)?.full_name ?? person(n.mentioned_to)?.email ?? '—'}
              </span>
            )}
            {mine(n) && (
              <button className="actes-row-act danger" title={T('Supprimer', 'Delete')} onClick={() => setAsk(n)}>
                <KBIcon name="trash" size={12} />
              </button>
            )}
          </div>
          <p className="actes-note-b">{n.body}</p>
        </div>
      ))}

      {reference.me.isStaff && compact && !writing && (
        <button type="button" className="actes-note-open"
          onClick={() => { setWriting(true); window.setTimeout(() => box.current?.focus(), 0); }}>
          <KBIcon name="edit" size={13} /> {T('Écrire une note', 'Write a note')}
        </button>
      )}

      {reference.me.isStaff && (!compact || writing) && (
        /* ÉCRIRE UNE NOTE EST UNE SEULE CHOSE, DONC UN SEUL CADRE.
           C'étaient trois morceaux posés l'un sous l'autre — une pastille,
           un champ, et une barre avec un « @ » tout seul d'un côté et
           « Ajouter » de l'autre — qui ne se lisaient pas comme un endroit
           où l'on écrit. Un cadre, la personne prévenue en haut, le texte au
           milieu, les commandes dans le pied du même cadre. */
        <div className={`actes-note-new ${focus ? 'on' : ''}`}>
          {mentioned && (
            <div className="actes-note-to">
              <span className="cap">{T('Prévenir', 'Notify')}</span>
              <span className="actes-note-chip">
                <ActesAvatar person={mentioned} size="sm" />
                {mentioned.full_name ?? mentioned.email}
                <button onClick={() => setNotify('')} aria-label={T('Retirer', 'Remove')}>×</button>
              </span>
            </div>
          )}
          <textarea ref={box} rows={3} value={draft} onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
            placeholder={T('Écrire une note…', 'Write a note…')}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void add(); }} />
          <div className="actes-note-act">
            <div className="actes-note-who">
              <button type="button" className={`actes-note-tag ${who ? 'on' : ''}`}
                title={T('Prévenir quelqu’un', 'Tag somebody')} onClick={() => setWho((v) => !v)}>
                <span>@</span> {T('Prévenir', 'Notify')}
              </button>
              {who && (
                <>
                  <div className="actes-cols-scrim" onClick={() => setWho(false)} />
                  <div className="actes-cols-pop" role="listbox">
                    <div className="cap">{T('Prévenir', 'Notify')}</div>
                    {reference.profiles.map((p) => (
                      <button key={p.id} className="actes-combo-row" onClick={() => { setNotify(p.id); setWho(false); box.current?.focus(); }}>
                        <ActesAvatar person={p} size="sm" />
                        <span>{p.full_name ?? p.email}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            {/* Le raccourci existait déjà et personne ne pouvait le deviner. */}
            <span className="actes-note-kbd">⌘ / Ctrl + ⏎</span>
            <button className="btn btn-sm" disabled={busy || !draft.trim()} onClick={() => void add()}>
              {busy ? '…' : T('Ajouter', 'Add')}
            </button>
          </div>
        </div>
      )}

      {err && <p className="muted-note ni-err">⚠ {err}</p>}

      {ask && (
        <div className="actes-ask">
          <div className="actes-ask-card">
            <h4>{T('Supprimer cette note ?', 'Delete this note?')}</h4>
            <p className="muted-note">{ask.body.slice(0, 80)}{ask.body.length > 80 ? '…' : ''}</p>
            <div className="nt-actions">
              <button className="btn btn-ghost" onClick={() => setAsk(null)}>{T('Annuler', 'Cancel')}</button>
              <button className="btn" disabled={busy} onClick={() => void remove()}>{T('Supprimer', 'Delete')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
